// src/contexts/ThemeContext.js - Theme context for dark/light mode
import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && savedTheme !== 'system') {
      return savedTheme;
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  const getEffectiveTheme = (themePreference) => {
    if (themePreference === 'system') {
      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    return themePreference || 'light';
  };

  const [themePreference, setThemePreference] = useState(() => {
    return localStorage.getItem('theme') || 'system';
  });

  const [theme, setTheme] = useState(() => getInitialTheme());

  // Update effective theme when preference changes
  useEffect(() => {
    const effectiveTheme = getEffectiveTheme(themePreference);
    setTheme(effectiveTheme);
  }, [themePreference]);

  // Listen for system theme changes when preference is 'system'
  useEffect(() => {
    if (themePreference !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        setTheme(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } catch (e) {
      console.warn('Could not set up theme listener:', e);
    }
  }, [themePreference]);

  useEffect(() => {
    // Apply theme to document
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      if (theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
    // Save preference to localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('theme', themePreference);
      } catch (e) {
        console.warn('Could not save theme to localStorage:', e);
      }
    }
  }, [theme, themePreference]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        // Only auto-update if user hasn't manually set a preference
        try {
          if (!localStorage.getItem('theme')) {
            setTheme(e.matches ? 'dark' : 'light');
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } catch (e) {
      console.warn('Could not set up theme listener:', e);
    }
  }, []);

  const toggleTheme = () => {
    const currentEffective = getEffectiveTheme(themePreference);
    const newPreference = currentEffective === 'light' ? 'dark' : 'light';
    setThemePreference(newPreference);
  };

  const applyThemePreference = (preference) => {
    setThemePreference(preference);
  };

  const value = {
    theme,
    themePreference,
    setTheme: applyThemePreference,
    toggleTheme,
    isDark: theme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

